import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { ApiError } from '@/core/api';
import { COUNTRIES } from '@/lib/countries';
import type { CompanyBankAccount } from '../api';
import {
  useCompanyBankAccounts,
  useCreateCompanyBankAccount,
  useDeleteCompanyBankAccount,
  useUpdateCompanyBankAccount,
} from '../queries';

const schema = z.object({
  label: z.string().max(120),
  account_holder: z.string().max(150),
  bank_name: z.string().max(150),
  bank_address: z.string(),
  iban: z.string().max(42),
  swift_bic: z.string().max(11),
  account_number: z.string().max(64),
  routing_number: z.string().max(34),
  account_type: z.string().max(20),
  currency: z.string().max(3),
  country: z.string().max(2),
  is_default: z.boolean(),
  notes: z.string().max(255),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  label: '',
  account_holder: '',
  bank_name: '',
  bank_address: '',
  iban: '',
  swift_bic: '',
  account_number: '',
  routing_number: '',
  account_type: '',
  currency: '',
  country: '',
  is_default: false,
  notes: '',
};

const COUNTRY_NONE = '__none__';

function toFormValues(account: CompanyBankAccount): FormValues {
  return {
    label: account.label,
    account_holder: account.account_holder,
    bank_name: account.bank_name,
    bank_address: account.bank_address,
    iban: account.iban,
    swift_bic: account.swift_bic,
    account_number: account.account_number,
    routing_number: account.routing_number,
    account_type: account.account_type,
    currency: account.currency,
    country: account.country,
    is_default: account.is_default,
    notes: account.notes,
  };
}

export function CompanyBankAccountsCard() {
  const { data: accounts, isPending, isError, error } = useCompanyBankAccounts();
  const create = useCreateCompanyBankAccount();
  const update = useUpdateCompanyBankAccount();
  const del = useDeleteCompanyBankAccount();

  const [editing, setEditing] = useState<CompanyBankAccount | null>(null);
  const [open, setOpen] = useState(false);

  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });
  const { reset } = form;

  useEffect(() => {
    if (open) reset(editing ? toFormValues(editing) : EMPTY);
  }, [open, editing, reset]);

  function openCreate() {
    setEditing(null);
    setOpen(true);
  }

  function openEdit(account: CompanyBankAccount) {
    setEditing(account);
    setOpen(true);
  }

  async function handleSubmit(values: FormValues) {
    try {
      if (editing) {
        await update.mutateAsync({ publicId: editing.public_id, input: values });
      } else {
        await create.mutateAsync(values);
      }
      setOpen(false);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field in EMPTY ? (field as keyof FormValues) : 'root', {
            message: messages.join(' '),
          });
        }
      }
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Company bank accounts</CardTitle>
          <CardDescription>
            Receiving accounts printed on client invoices. The default account is used on the PDF.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus /> Add account
        </Button>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : isError ? (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load bank accounts.'}
          </p>
        ) : accounts.length === 0 ? (
          <p className="text-muted-foreground text-sm">No bank accounts yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {accounts.map((account) => (
              <li key={account.public_id} className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    <span className="truncate">
                      {account.label || account.bank_name || account.iban || 'Account'}
                    </span>
                    {account.is_default ? (
                      <span className="bg-primary/10 text-primary rounded px-1.5 py-0.5 text-xs">
                        Default
                      </span>
                    ) : null}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {[account.account_holder, account.iban || account.account_number, account.currency]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${account.label || 'bank account'}`}
                    onClick={() => openEdit(account)}
                  >
                    <Pencil />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Delete ${account.label || 'bank account'}`}
                      >
                        <Trash2 />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this bank account?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => del.mutate(account.public_id)}
                          disabled={del.isPending}
                        >
                          {del.isPending ? 'Deleting…' : 'Delete'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit bank account' : 'Add bank account'}</DialogTitle>
            <DialogDescription>
              Leave fields blank when they do not apply to this account.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Label</FormLabel>
                    <FormControl>
                      <Input placeholder="Main account" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="account_holder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account holder</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="bank_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Bank name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="iban"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>IBAN</FormLabel>
                      <FormControl>
                        <Input className="font-mono" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="swift_bic"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>SWIFT / BIC</FormLabel>
                      <FormControl>
                        <Input className="font-mono" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="account_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account number</FormLabel>
                      <FormControl>
                        <Input className="font-mono" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="routing_number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Routing / sort code</FormLabel>
                      <FormControl>
                        <Input className="font-mono" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <FormField
                  control={form.control}
                  name="account_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account type</FormLabel>
                      <FormControl>
                        <Input placeholder="Checking" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Currency</FormLabel>
                      <FormControl>
                        <Input placeholder="USD" maxLength={3} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country</FormLabel>
                      <Select
                        value={field.value || COUNTRY_NONE}
                        onValueChange={(v) => field.onChange(v === COUNTRY_NONE ? '' : v)}
                      >
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={COUNTRY_NONE}>—</SelectItem>
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="bank_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bank address</FormLabel>
                    <FormControl>
                      <Textarea rows={2} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="is_default"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-3 rounded-md border p-3">
                    <FormLabel className="m-0">Default account (used on invoices)</FormLabel>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              {form.formState.errors.root ? (
                <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
              ) : null}
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={create.isPending || update.isPending}
                >
                  {create.isPending || update.isPending ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
